import { useEffect } from 'react';
import { cn } from '@/lib/cn';

const TERMS: Array<{
  abbr: string;
  full: string;
  desc: string;
}> = [
  {
    abbr: 'TUS',
    full: 'Total Units Sold',
    desc: 'Unidades totales vendidas en el día (suma de los 12 SKUs de empanadas).',
  },
  {
    abbr: 'NSR',
    full: 'Net Sales Revenue',
    desc: 'Ingresos netos del día = tarjeta + efectivo + rappi + uber.',
  },
  {
    abbr: 'ATS',
    full: 'Average Ticket Size',
    desc: 'Ticket promedio: NSR / TUS. Cuánto se gasta en promedio por empanada.',
  },
  {
    abbr: 'DPR',
    full: 'Digital Penetration Rate',
    desc: '% de ventas pagadas por medios digitales (tarjeta + rappi + uber).',
  },
  {
    abbr: 'CHE',
    full: 'Cash Handling Exposure',
    desc: '% de ventas cobradas en efectivo. Exposición a riesgo de cuadre de caja.',
  },
  {
    abbr: 'DQS',
    full: 'Data Quality Score',
    desc: 'Puntaje 0-100 que mide la calidad de la data: SKUs faltantes, drift en pagos, varianza de caja.',
  },
  {
    abbr: 'NOI',
    full: 'Net Operating Income',
    desc: 'Utilidad operativa = NSR - COGS - OpEx (gastos del mes).',
  },
  {
    abbr: 'COGS',
    full: 'Cost of Goods Sold',
    desc: 'Costo de lo vendido = TUS × costo unitario.',
  },
  {
    abbr: 'BEP',
    full: 'Break-Even Point',
    desc: 'Punto de equilibrio: unidades/ingresos mínimos para cubrir costos fijos.',
  },
  {
    abbr: 'CV',
    full: 'Coefficient of Variation',
    desc: 'Desviación estándar / media. Mide predictibilidad. <0.3 predecible, 0.3-0.5 moderado, >0.5 inestable.',
  },
  {
    abbr: 'MAPE',
    full: 'Mean Absolute Percentage Error',
    desc: 'Error % promedio del modelo vs realidad. <10% excelente, 10-20% bueno, >20% ajustar.',
  },
  {
    abbr: 'RMSE',
    full: 'Root Mean Square Error',
    desc: 'Raíz del error cuadrático medio. Penaliza más los errores grandes que MAE.',
  },
  {
    abbr: 'EMA',
    full: 'Exponential Moving Average',
    desc: 'Suavizado exponencial: F_t = α·A_{t-1} + (1-α)·F_{t-1}. Da más peso a datos recientes.',
  },
  {
    abbr: 'IC 90%',
    full: 'Intervalo de Confianza al 90%',
    desc: 'Banda pronostico ± 1.645 × error estándar. Hay 90% de probabilidad de que el real caiga dentro.',
  },
  {
    abbr: 'ROP',
    full: 'Reorder Point',
    desc: 'Punto de reorden: (demanda_diaria × lead_time) + stock_seguridad. Cuando stock ≤ ROP, reordenar.',
  },
  {
    abbr: 'Stock Seg.',
    full: 'Stock de Seguridad',
    desc: 'Z × σ × √(Lead Time) con Z=1.96 (95% servicio). Protege contra variabilidad de demanda.',
  },
  {
    abbr: 'YoY',
    full: 'Year over Year',
    desc: 'Comparación anual: mismo mes del año anterior vs mes actual. Mide crecimiento real.',
  },
  {
    abbr: 'P&L',
    full: 'Profit & Loss',
    desc: 'Estado de resultados: ingresos, costos, gastos, utilidad bruta, NOI.',
  },
  {
    abbr: 'TEI',
    full: 'Teorethical Ending Inventory',
    desc: 'Inventario final teórico: E_inicial + compras - ventas. Compara contra inventario real para detectar merma.',
  },
  {
    abbr: 'DOH',
    full: 'Days on Hand',
    desc: 'Días de inventario disponibles al ritmo de venta actual. TEI / promedio_ventas_diarias.',
  },
  {
    abbr: 'What-If',
    full: 'Análisis de sensibilidad',
    desc: 'Escenarios pesimista (0.75×) / probable (1×) / optimista (1.25×) sobre el pronóstico base.',
  },
];

const MODULES: Array<{ code: string; label: string }> = [
  { code: '01', label: 'Ventas' },
  { code: '02', label: 'Finanzas' },
  { code: '03', label: 'Inventario' },
  { code: '04', label: 'Anomalías' },
  { code: '05', label: 'Predicción' },
  { code: '06', label: 'Carga' },
  { code: '07', label: 'Configuración' },
];

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

export function HelpModal({ open, onClose }: HelpModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.85)' }}
      onClick={onClose}
    >
      <div
        className={cn(
          'bg-surface border-2 border-primary max-w-4xl w-full max-h-[90vh] overflow-hidden',
          'shadow-[8px_8px_0_0_#000000]',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b-2 border-outline">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // SYSTEM_HANDBOOK :: v1.0
            </p>
            <h2 className="font-condensed text-3xl uppercase tracking-tight text-primary mt-1">
              Glosario & Guía Rápida
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-surface border-2 border-outline text-text-bright hover:border-error hover:text-error transition-colors duration-150"
            aria-label="Cerrar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-100px)] custom-scrollbar">
          {/* About */}
          <section className="p-5 border-b-2 border-outline bg-surface-container">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
              // ABOUT
            </p>
            <h3 className="font-condensed text-2xl uppercase text-text-bright mb-2">
              Empanada BI · Kinetic Overload
            </h3>
            <p className="text-sm text-text-bright font-mono leading-relaxed">
              Sistema de inteligencia de negocio para un local de empanadas QSR.
              Centraliza la información operativa (ventas, inventario, caja)
              en un solo panel, y proyecta ventas futuras usando forecasting
              con day-of-week + day-of-month + trend + estacionalidad.
            </p>
            <p className="text-xs text-text-muted font-mono leading-relaxed mt-3">
              Los datos se suben como archivos Excel mensuales (un archivo por
              mes). El parser los normaliza, calcula métricas por día y SKU,
              y la base de datos persiste todo para análisis y predicción.
            </p>
          </section>

          {/* Modules */}
          <section className="p-5 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-3">
              // MÓDULOS
            </p>
            <h3 className="font-condensed text-2xl uppercase text-text-bright mb-3">
              Módulos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {MODULES.map((m) => (
                <div
                  key={m.code}
                  className="bg-surface border-2 border-outline p-3"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    {m.code}
                  </p>
                  <p className="font-bold uppercase tracking-wider text-sm text-text-bright">
                    {m.label}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Glossary */}
          <section className="p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-3">
              // GLOSARIO :: {TERMS.length} TÉRMINOS
            </p>
            <h3 className="font-condensed text-2xl uppercase text-text-bright mb-3">
              Términos y Abreviaciones
            </h3>
            <div className="space-y-2">
              {TERMS.map((t) => (
                <div
                  key={t.abbr}
                  className="bg-surface border-2 border-outline p-3 flex flex-col md:flex-row md:items-start gap-2 md:gap-4"
                >
                  <div className="md:w-40 shrink-0">
                    <span className="inline-block px-2 py-1 bg-primary text-black border-2 border-black font-mono text-xs uppercase tracking-widest font-bold">
                      {t.abbr}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-text-bright text-sm font-mono uppercase tracking-wider">
                      {t.full}
                    </p>
                    <p className="text-text-muted text-xs mt-1 font-mono leading-relaxed">
                      {t.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Footer */}
          <section className="p-5 border-t-2 border-outline bg-surface-container">
            <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted text-center">
              // Built with NestJS + Prisma + MySQL + React + Tailwind //
            </p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted text-center mt-1">
              Press <span className="text-primary">Esc</span> to close
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
